import { getRequestHeaders } from "./headers";
import { ensureFreshSession } from "./session";
declare const API_URL: string;

/** Base path for all API calls (includes stage prefix). */
export const API_BASE = `${API_URL}/prod/api`;

/**
 * fetch() wrapper for API calls that transparently recovers from a lapsed
 * session. On an auth failure it triggers a silent Federate token renewal (see
 * services/session.ts) and, if a renewal actually happened, retries the request
 * once with the fresh cookie. During normal operation the background renewal
 * keeps the token fresh, so this is a rare safety net for the brief window right
 * at token expiry. Genuine permission errors (403 while still authenticated) are
 * passed straight through, since no renewal occurs.
 */
export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
    const res = await fetch(input, init);
    if (res.status === 401 || res.status === 403) {
        const renewed = await ensureFreshSession();
        if (renewed) {
            return fetch(input, init);
        }
    }
    return res;
}

export async function handleError(res: Response): Promise<never> {
    let errorMessage: string;
    try {
        const body = await res.json();
        errorMessage = body.error || `HTTP ${res.status}`;
    } catch {
        errorMessage = `HTTP ${res.status}: ${res.statusText}`;
    }

    throw new Error(errorMessage);
}

export interface SectionPermission {
    read: boolean;
    write: boolean;
}

export interface SectionPermissions {
    globalShiftPassdown: SectionPermission;
    alarmsShiftPassdown: SectionPermission;
    csShiftPassdown: SectionPermission;
    csirShiftPassdown: SectionPermission;
    cmShiftPassdown: SectionPermission;
    eventsShiftPassdown: SectionPermission;
    oimShiftPassdown: SectionPermission;
    announcements: SectionPermission;
    calendar: SectionPermission;
    sopUpdates: SectionPermission;
}

export interface UserInfo {
    alias: string;
    email: string;
    permissions: string[];
    sectionPermissions: SectionPermissions;
}

export interface Item {
    pk: string;
    sk: string;
    [key: string]: any;
}

export interface PaginatedItems {
    items: Item[];
    nextToken?: string;
}

export async function fetchCurrentUser(useOverride = true): Promise<UserInfo | null> {
    try {
        // When useOverride is false we omit the x-role-override header so the backend
        // returns the user's REAL permissions (used to detect real admin status).
        const headers = useOverride ? getRequestHeaders() : {};
        // apiFetch handles silent session renewal + retry on a lapsed token.
        const res = await apiFetch(`${API_BASE}/users/me`, { headers });
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

export async function fetchItems(all = true): Promise<PaginatedItems> {
    const res = await apiFetch(`${API_BASE}/items${all ? '?all=true' : ''}`);
    if (!res.ok) await handleError(res);
    return await res.json();
}
